import { createTheme } from '@mui/material/styles';

export const COLORS = {
  red: '#DD4F25',
  primary: '#62B8E9',
  blue: '#62B8E9',
  green: '#1CBA19',
  darkBlue: '#2C2C2C', // Updated to match styleguide
  darkGray: '#E8E8E8', // Updated to match styleguide
  black: '#2C2C2C', // Updated to match styleguide
  gray: '#ECEDF0',
  mediumGray: '#e1e3e6',
  white: '#FFFFFF',
  lightRed: '#FCEDEE',
  lightOrange: '#FFF6E5',
  lightGreen: '#EAF3E1',
  lightGray: '#F9FCFF',
  lightBlack: '#6C6C6C' // Updated to match styleguide
};

const fontFamily = "'Poppins', sans-serif";

const defaultTheme = createTheme();

const palette = {
  primary: {
    main: COLORS.primary
  },
  secondary: {
    main: COLORS.green
  },
  error: {
    main: COLORS.red
  },
  warning: {
    main: COLORS.primary
  },
  info: {
    main: COLORS.primary
  },
  success: {
    main: COLORS.green
  },
  text: {
    primary: COLORS.black,
    secondary: COLORS.darkBlue,
    disabled: COLORS.darkBlue
  }
};

const typography = {
  h1: {
    fontFamily,
    fontWeight: 600,
    fontSize: '32px',
    color: COLORS.primary,
    [defaultTheme.breakpoints.down('lg')]: {
      fontSize: '28px'
    }
  },
  h2: {
    fontFamily,
    fontWeight: 600,
    fontSize: '28px',
    color: COLORS.primary,
    letterSpacing: '0.02em',
    [defaultTheme.breakpoints.down('lg')]: {
      fontSize: '24px'
    }
  },
  h3: {
    fontFamily,
    fontWeight: 600,
    fontSize: '24px',
    color: COLORS.primary,
    lineHeight: '100%',
    letterSpacing: '0.02em',
    [defaultTheme.breakpoints.down('lg')]: {
      fontSize: '20px'
    }
  },
  h4: {
    fontFamily,
    fontWeight: 600,
    fontSize: '16px',
    color: COLORS.primary,
    [defaultTheme.breakpoints.down('lg')]: {
      fontSize: '14px'
    }
  },
  h5: {
    fontFamily,
    fontWeight: 600,
    fontSize: '14px',
    color: COLORS.primary,
    lineHeight: '20px'
  },
  h6: {
    fontFamily,
    fontWeight: 600,
    fontSize: '12px',
    color: COLORS.primary,
    marginTop: '24px',
    marginBottom: '12px',
    [defaultTheme.breakpoints.down('lg')]: {
      marginTop: '20px',
      marginBottom: '12px'
    }
  },
  subtitle1: { fontFamily },
  subtitle2: { fontFamily },
  body1: { fontFamily, lineHeight: '20px' },
  body2: { fontFamily },
  caption: { fontFamily },
  button: { fontFamily },
  overline: { fontFamily }
};

const theme = createTheme({
  colors: COLORS,
  palette,
  typography,
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily,
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '20px',
          borderRadius: '10px',
          padding: '12px 20px',
          '&.Mui-disabled': {
            opacity: '0.2'
          }
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          fontFamily,
          flex: 1,
          width: '100%',
          height: '100%',
          padding: '0px !important'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily,
          top: 6,
          '&.Mui-focused': {
            top: 12,
            color: COLORS.darkGray
          },
          '&.MuiInputLabel-filled': {
            top: 12,
            color: COLORS.darkGray,
            fontSize: '12px'
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.MuiFormLabel-filled': {
            top: 12,
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontFamily, color: COLORS.lightBlack }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily,
          borderRadius: 0,
          backgroundColor: COLORS.lightGray,
          borderColor: COLORS.lightGray,
          color: COLORS.black,
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 500,
          '.MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${COLORS.darkGray}`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${COLORS.black}`
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.black
          },
          'legend > span': {
            display: 'none'
          }
        },
        multiline: {
          '.MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${COLORS.black}`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${COLORS.black}`
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: `2px solid ${COLORS.black}`
          }
        },
        input: {
          height: '13px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          marginRight: '20px',
          '& .MuiSvgIcon-root': {
            fontSize: 28
          }
        }
      }
    }
  }
});

export default theme;
